/**
 * Pure helpers for the group-invite flow (Phase 12). No Firebase, no React —
 * kept side-effect-free so they're cheap to unit-test (inviteHelpers.test.js).
 */

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

const DEFAULT_COLOR = '#8C8AFD';
const DEFAULT_EMOJI = '🧾';

function isHex(s) {
  return typeof s === 'string' && HEX_RE.test(s);
}

/**
 * First character of a name, uppercased — used for monogram avatars. The invite
 * preview returns name + color per member (no per-member emoji), so the web
 * renders initials. Empty/blank names fall back to '?'.
 */
export function initialOf(name) {
  const trimmed = (typeof name === 'string' ? name : '').trim();
  return trimmed ? trimmed[0].toUpperCase() : '?';
}

/**
 * Maps a groupsGetInvitePreview payload to the view model the landing renders.
 * A missing/deleted group (found !== true) or a null payload maps to
 * { status: 'unavailable' } so the UI can show a friendly state. Otherwise
 * returns { status: 'ok', groupName, emoji, accent, inviter, members, memberCount }
 * with every field defaulted so the renderer never sees undefined.
 */
export function mapPreviewToView(preview) {
  if (!preview || preview.found !== true) {
    return { status: 'unavailable' };
  }

  const members = Array.isArray(preview.members) ? preview.members : [];

  return {
    status: 'ok',
    groupName: preview.groupName || 'this group',
    emoji: preview.emoji || DEFAULT_EMOJI,
    accent: isHex(preview.accent) ? preview.accent : DEFAULT_COLOR,
    inviter: preview.inviter
      ? {
          name: preview.inviter.name || 'Someone',
          color: isHex(preview.inviter.color) ? preview.inviter.color : DEFAULT_COLOR,
          initial: initialOf(preview.inviter.name),
        }
      : null,
    members: members.map((m) => ({
      name: m && m.name ? m.name : 'Member',
      color: m && isHex(m.color) ? m.color : DEFAULT_COLOR,
      initial: initialOf(m && m.name),
    })),
    memberCount:
      typeof preview.memberCount === 'number' ? preview.memberCount : members.length,
  };
}

/**
 * Normalize the name typed on the join name screen (JoinNameContainer):
 * trimmed, 1 to 40 chars, required. Returns the cleaned name, or '' when
 * invalid, so callers can use truthiness as the validity check. The input
 * caps raw length at 40, but guard here too so the write can never carry
 * an over-long or whitespace-only name.
 */
export function normalizeJoinName(raw) {
  const trimmed = (typeof raw === 'string' ? raw : '').trim();
  if (!trimmed || trimmed.length > 40) return '';
  return trimmed;
}

/**
 * Resolve with `fallback` when `promise` takes longer than `ms`. A cold
 * Firestore read can stall for minutes without rejecting, and the join flow
 * must never hold a person behind a spinner on a name check: every check
 * gives up quickly and fails toward letting them continue.
 */
export function withTimeout(promise, ms, fallback) {
  let timer;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => resolve(fallback), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/**
 * The invite link's query params. The app mints /groups/join/{gid}?inviter={uid};
 * the texted short link's /r/ redirect appends i={code}, the invite id the
 * tracking plan carries from the text all the way to group_joined; utm_source
 * rides along when a link has one. All optional; absent values come back undefined.
 * @param {string | null | undefined} search location.search
 * @returns {{ inviter?: string, inviteCode?: string, utmSource?: string }}
 */
export function readInviteParams(search) {
  const params = new URLSearchParams(search || '');
  return {
    inviter: params.get('inviter') || undefined,
    inviteCode: params.get('i') || undefined,
    utmSource: params.get('utm_source') || undefined,
  };
}

/**
 * The query string that carries the invite params from screen to screen
 * (landing -> phone -> verify -> name -> app). '' when there is nothing to carry.
 * @param {{ inviter?: string, inviteCode?: string, utmSource?: string }} [params]
 * @returns {string}
 */
export function buildInviteQuery({ inviter, inviteCode, utmSource } = {}) {
  const params = new URLSearchParams();
  if (inviter) params.set('inviter', inviter);
  if (inviteCode) params.set('i', inviteCode);
  if (utmSource) params.set('utm_source', utmSource);
  const query = params.toString();
  return query ? `?${query}` : '';
}

/**
 * Pure builder for the pending-join payload written to users/{uid}.pendingGroupJoin.
 * The web records intent; the APP completes the join on next login. `createdAt` is
 * injected by the caller (firebase serverTimestamp) so this stays pure/testable.
 * `inviter` and `inviteCode` are normalized to null when absent (Firestore rejects
 * undefined). `inviteCode` is the short link code from ?i=, so the app can stamp
 * invite_id on group_joined.
 */
export function buildPendingGroupJoin(gid, inviter, createdAt, inviteCode) {
  return {
    gid,
    inviter: inviter || null,
    inviteCode: inviteCode || null,
    createdAt: createdAt === undefined ? null : createdAt,
  };
}
